// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { TitleCard } from '../components/common/card';
import EmptyContainer from '../components/common/empty/EmptyContainer';
import { PageError } from '../components/common/error';
import { PageLoading } from '../components/common/loading';
import AddRecurringPlantingDialog from '../components/recurringPlanting/AddRecurringPlantingDialog';
import RecurringPlantingTable from '../components/recurringPlanting/RecurringPlantingTable';
import { useGating, useUserContext } from '../context/UserContext';
import { Button, Typography } from '../lib/base';
import { AddIcon } from '../lib/icons';
import { RECURRING_PLANTINGS_QUERY } from '../queries/RecurringPlantingQueries';
import type { RecurringPlantingsHomeQuery, RecurringPlantingsHomeQueryVariables } from '../types/__generated__/graphql';

export default React.memo(function RecurringPlantings() {
  const { data, error, loading } = useQuery<RecurringPlantingsHomeQuery, RecurringPlantingsHomeQueryVariables>(
    RECURRING_PLANTINGS_QUERY,
  );
  const { canUseBasic } = useGating();
  const { permissions } = useUserContext();
  const [newRecurringPlantingOpen, setNewRecurringPlantingOpen] = React.useState(false);

  const userCanEditPlantings = permissions.can_edit_orders;

  const content = React.useMemo(() => {
    if (loading) {
      return <PageLoading />;
    }
    if (error) {
      return <PageError error={error} />;
    }
    if (data == null || data.recurring_plantings == null) {
      return <PageError />;
    }
    const plantings = data.recurring_plantings.edges.map((edge) => edge.node);
    if (plantings.length === 0) {
      return (
        <EmptyContainer
          title="You have no recurring plantings!"
          description={'Click the "New Planting +" button to start creating recurring plantings.'}
        />
      );
    }

    return <RecurringPlantingTable plantings={plantings} />;
  }, [loading, data, error]);

  const handleNewPlantingClick = React.useCallback(() => setNewRecurringPlantingOpen(true), []);
  const handlePlantingDialogClose = React.useCallback(() => setNewRecurringPlantingOpen(false), []);

  const options = React.useMemo(
    () =>
      userCanEditPlantings ? (
        <Button
          id="new-planting-button"
          variant="contained"
          onClick={handleNewPlantingClick}
          endIcon={<AddIcon sx={{ color: 'secondary.contrastText' }} />}
          color="secondary"
          disabled={!canUseBasic}
        >
          <Typography variant="body1" sx={{ color: 'secondary.contrastText' }}>
            New Planting
          </Typography>
        </Button>
      ) : (
        <></>
      ),
    [userCanEditPlantings, handleNewPlantingClick, canUseBasic],
  );

  return (
    <>
      <Helmet>
        <title>{'Recurring Plantings'}</title>
      </Helmet>
      <TitleCard title="Recurring Plantings" rightElement={options} content={content} />
      {newRecurringPlantingOpen && (
        <AddRecurringPlantingDialog
          isOpen={newRecurringPlantingOpen}
          onClose={handlePlantingDialogClose}
          onSuccess={handlePlantingDialogClose}
        />
      )}
    </>
  );
});
